import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Loader, Text, Title, UnstyledButton } from '@mantine/core';
import { IconLogout } from '@tabler/icons-react';
import { contextsTestIds } from '@contexts/testids';
import useAuth from './useAuth';
import styles from './ProvisioningOverlay.module.css';

export function ProvisioningOverlay() {
  const { t } = useTranslation( 'authentication' );
  const auth = useAuth();

  if ( !auth ) return null;
  const { isProvisioning, provisioningError, retryProvisioning, signout } = auth;

  if ( !isProvisioning && !provisioningError ) return null;

  // Error: centered message + actions, with a corner sign-out escape hatch.
  if ( provisioningError ) {
    return (
      <>
        <div className={styles.overlay} data-testid={contextsTestIds.provisioningOverlay} data-error="true">
          <div className={styles.text} data-testid={contextsTestIds.provisioningError}>
            <Title order={3} className={styles.title} data-testid={contextsTestIds.provisioningTitle}>
              {t( 'provisioning_error_title' )}
            </Title>
            <Text className={styles.body}>
              {t( 'provisioning_error_body' )}
            </Text>
          </div>
          <div className={styles.actions}>
            <Button
              variant="light"
              size="sm"
              radius="sm"
              className={styles.cancelButton}
              component="a"
              href="mailto:support@teselly.com"
              data-testid={contextsTestIds.provisioningSupportLink}
            >
              {t( 'provisioning_contact_support' )}
            </Button>
            <Button
              variant="filled"
              size="sm"
              radius="sm"
              onClick={retryProvisioning}
              data-testid={contextsTestIds.provisioningRetryButton}
            >
              {t( 'provisioning_retry' )}
            </Button>
          </div>
        </div>

        <UnstyledButton
          className={styles.logoutButton}
          onClick={() => signout()}
          data-testid={contextsTestIds.provisioningLogoutButton}
        >
          <IconLogout size={16} />
          <span>{t( 'provisioning_logout', 'Cerrar sesión' )}</span>
        </UnstyledButton>
      </>
    );
  }

  // Loading: centered spinner + status message.
  return (
    <div className={styles.overlay} data-testid={contextsTestIds.provisioningOverlay}>
      <Loader size="sm" data-testid={contextsTestIds.provisioningLoader} />
      <div className={styles.text}>
        <Title order={3} className={styles.title} data-testid={contextsTestIds.provisioningTitle}>
          {t( 'provisioning_title' )}
        </Title>
        <Text className={styles.body}>
          {t( 'provisioning_subtitle' )}
        </Text>
      </div>
    </div>
  );
}
